'use client';

import { useState, Suspense } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Loader2, Mail } from 'lucide-react';

type Method = 'password' | 'magic';

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Default to /post-login which inspects the session and routes to /admin or /dashboard
  // based on role. Specific callbackUrl (from middleware redirect) wins over default.
  const callbackUrl = searchParams.get('callbackUrl') ?? '/post-login';

  const [method, setMethod] = useState<Method>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const result = await signIn('credentials', {
      email,
      password,
      redirect: false,
    });

    if (result?.error) {
      setError('Onjuist e-mailadres of wachtwoord.');
      setLoading(false);
      return;
    }

    router.push(callbackUrl);
    router.refresh();
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const result = await signIn('email', {
      email,
      redirect: false,
      callbackUrl,
    });

    if (result?.error) {
      setError('Kon geen inlog-link versturen. Probeer opnieuw.');
      setLoading(false);
      return;
    }

    router.push('/login/check-email');
  }

  return (
    <div className="w-full max-w-md">
      <div className="card p-8">
        <h1 className="text-2xl font-bold tracking-tight">Welkom terug</h1>
        <p className="text-ink-2 text-sm mt-1 mb-6">Log in op je Castly account.</p>

        {/* Method tabs */}
        <div className="flex gap-1 p-1 bg-bg-3 rounded-lg mb-6">
          <button
            onClick={() => {
              setMethod('password');
              setError(null);
            }}
            className={`flex-1 py-1.5 text-xs rounded-md transition-colors ${
              method === 'password' ? 'bg-white shadow-sm font-medium' : 'text-ink-2'
            }`}
          >
            Wachtwoord
          </button>
          <button
            onClick={() => {
              setMethod('magic');
              setError(null);
            }}
            className={`flex-1 py-1.5 text-xs rounded-md transition-colors ${
              method === 'magic' ? 'bg-white shadow-sm font-medium' : 'text-ink-2'
            }`}
          >
            Magic link
          </button>
        </div>

        {method === 'password' ? (
          <form onSubmit={handlePasswordLogin} className="space-y-4">
            <Field label="E-mailadres">
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="jij@bedrijf.nl"
              />
            </Field>
            <Field label="Wachtwoord">
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
              />
            </Field>
            <div className="text-right -mt-2">
              <Link
                href="/forgot-password"
                className="text-xs text-ink-2 hover:text-primary hover:underline"
              >
                Wachtwoord vergeten?
              </Link>
            </div>
            {error && <ErrorBox>{error}</ErrorBox>}
            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? <Loader2 size={16} className="animate-spin" /> : 'Inloggen'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleMagicLink} className="space-y-4">
            <p className="text-sm text-ink-2 leading-relaxed">
              We sturen je een inlog-link via e-mail. Geen wachtwoord nodig.
            </p>
            <Field label="E-mailadres">
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="jij@bedrijf.nl"
              />
            </Field>
            {error && <ErrorBox>{error}</ErrorBox>}
            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  <Mail size={16} /> Stuur inlog-link
                </>
              )}
            </button>
          </form>
        )}

        <div className="mt-6 pt-6 border-t border-border text-sm text-ink-2 text-center">
          Nog geen account?{' '}
          <Link href="/register" className="text-primary font-medium hover:underline">
            Maak er een aan
          </Link>
        </div>
      </div>

      <style jsx>{`
        .input {
          width: 100%;
          background: white;
          border: 1px solid var(--border-2);
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 14px;
        }
        .input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(13, 124, 102, 0.1);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-rose-soft text-rose text-sm rounded-md p-3 border border-rose/20">
      {children}
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="text-ink-2">Laden...</div>}>
      <LoginForm />
    </Suspense>
  );
}
